import {
  solutionStartChatIds,
  getBotPublishedEnvs,
  getCustomerPypes,
  resolveBotSolutionIds,
} from '../../utils';
import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';
import {
  getBotChannelEnvs,
  getSurveyLinkedSolutionIds,
} from './helpers/solutions';

export interface PublishModalData {
  /** Linked main-solution ids per env, to pre-select in the modal [PSTUDIO-2081]. */
  surveyLinkedSolutions: { live: string[]; sandbox: string[] };
  /**
   * Environments per non-survey candidate solution id where the survey can
   * actually be linked to it (e.g. `{ 'sol-1': ['sandbox', 'live'] }`). A main is
   * linkable in an env only when it has BOTH a channel bound to it (a stream with
   * `start_chat_bot_id === <customerName>.<uuid>`) AND a published version there — the
   * precondition for `updateSurveyBotStreams` to persist the link. Drives per-env
   * enable/disable and the env label in the main-solution <Select> [PSTUDIO-3210].
   */
  solutionEnvs: Record<string, string[]>;
}

const emptyPublishModalData = (): PublishModalData => ({
  surveyLinkedSolutions: { live: [], sandbox: [] },
  solutionEnvs: {},
});

/**
 * On publish-modal open for a survey, refresh everything the main-solution <Select>
 * depends on: (1) which main solutions this survey is linked to (per env, from pype
 * streams) [PSTUDIO-2081] and (2) which environments each non-survey candidate is
 * published to, so the option can be enabled only for the selected env and labelled
 * with its published env(s) [PSTUDIO-3210].
 *
 * The early `return` on missing ids / non-survey bot is an expected NO-OP (empty data),
 * not error suppression. Best-effort: callers must treat failure as non-blocking — that
 * guarantee depends on the machine wiring this invoke's `onError` to a non-fatal
 * transition (it returns to idle without a toast).
 */
export const loadPublishModalData = async (
  ctx: ProStudioContext,
  _event?: ProStudioEvents
): Promise<PublishModalData> => {
  const uniqueCustomerId = ctx.project?.projectProStudioSetting?.customerId;
  const projectId = ctx.project?.id;

  if (
    ctx.selectedBot?.botType !== 'survey' ||
    !uniqueCustomerId ||
    !projectId ||
    !ctx.customerId ||
    !ctx.botName
  ) {
    return emptyPublishModalData();
  }

  // The ids the survey's own streams — and every `end_chat_bot_id` linking a main to
  // it — may carry: `<customerName>.<uuid>`, plus the legacy `botSolutionId` for links
  // written before the migration (the backend never rewrote them).
  const surveyBotIds = resolveBotSolutionIds(ctx);

  if (!surveyBotIds.length) {
    return emptyPublishModalData();
  }

  const candidates = (ctx.solutions ?? []).filter(
    (sol) => sol.botType !== 'survey' && !!sol.id && !!sol.name
  );

  const [{ live, sandbox }, publishedSettled] = await Promise.all([
    getCustomerPypes({ uniqueCustomerId, projectId }),
    // Best-effort per candidate: one failed lookup must NOT blank the whole
    // modal (PSTUDIO-3210) — a rejected candidate degrades to "no published envs"
    // ([]) so the rest stay linkable. getCustomerPypes stays in Promise.all: without
    // the pypes we can't compute linkability at all, so that failure is fatal (the
    // machine's onError then clears any stale data).
    Promise.allSettled(
      candidates.map((sol) =>
        getBotPublishedEnvs({ solutionId: sol.id as string })
      )
    ),
  ]);

  // allSettled preserves order, so settle[i] lines up with candidates[i].
  const publishedEnvsBySolId = new Map(
    candidates.map((sol, i): [string, string[]] => {
      const settled = publishedSettled[i];
      return [
        sol.id as string,
        settled.status === 'fulfilled' ? settled.value : [],
      ];
    })
  );

  // Linkable env = has a bound channel stream (A) AND a published version (B).
  const solutionEnvs = Object.fromEntries(
    candidates.map((sol) => {
      const id = sol.id as string;
      const publishedEnvs = publishedEnvsBySolId.get(id) ?? [];
      const channelEnvs = getBotChannelEnvs(
        { live, sandbox },
        solutionStartChatIds(ctx.customerId, sol)
      );
      const linkableEnvs = channelEnvs.filter((env) =>
        publishedEnvs.includes(env)
      );
      return [id, linkableEnvs] as [string, string[]];
    })
  );

  return {
    surveyLinkedSolutions: {
      live: getSurveyLinkedSolutionIds(live, surveyBotIds, {
        bots: ctx.solutions,
        customerName: ctx.customerId,
      }),
      sandbox: getSurveyLinkedSolutionIds(sandbox, surveyBotIds, {
        bots: ctx.solutions,
        customerName: ctx.customerId,
      }),
    },
    solutionEnvs,
  };
};
